/**
 * MIDI devices. A binding names no device: every connected controller sending the same message drives the same binding. A device is identified only to tell a learn burst's messages apart and to label the inputs.
 */

/** A connected input's key: name + manufacturer + occurrence index (`index` tells two identical controllers apart). */
export const deviceKey = d => `${d?.manufacturer ?? ''}|${d?.name ?? ''}|${d?.index ?? 0}`

/** The router key for one control. `channel` is 1–16, or `'omni'` for any. */
export const midiKey = (channel, type, number) => `midi|${channel === 'omni' ? '*' : channel}|${type}|${number ?? 0}`

/**
 * Describe connected inputs as binding devices: `[{ portId, name, manufacturer, index, key }]`.
 *
 * `index` counts earlier ports with the same name and manufacturer, ordered by port id so it does not depend on enumeration order.
 */
export const describePorts = ports => {
	const sorted = [...ports].sort((a, b) => String(a.id).localeCompare(String(b.id)))
	const seen = new Map()
	return sorted.map(p => {
		const base = `${p.manufacturer ?? ''}|${p.name ?? ''}`
		const index = seen.get(base) ?? 0
		seen.set(base, index + 1)
		const d = { portId: p.id, name: p.name ?? '', manufacturer: p.manufacturer ?? '', index }
		return { ...d, key: deviceKey(d) }
	})
}

const WIN_IN = /^midiin(\d+) \((.*)\)$/

/**
 * The output half of a described input: the output under the same key, Windows' `MIDIOUT<n> (X)` for `MIDIIN<n> (X)`, else the input's occurrence among same-maker outputs whose name contains its name or sits inside it.
 */
export const matchOutput = (input, outputs) => {
	const same = outputs.find(o => o.key === input.key)
	if (same) return same
	const name = input.name.toLowerCase()
	if (!name) return undefined
	const win = WIN_IN.exec(name)
	if (win) {
		const out = outputs.find(o => o.name.toLowerCase() === `midiout${win[1]} (${win[2]})`)
		if (out) return out
	}
	const like = outputs.filter(o => {
		const n = o.name.toLowerCase()
		return o.manufacturer === input.manufacturer && n && (n.includes(name) || name.includes(n))
	})
	return like[input.index]
}

/** A device as a person reads it: its name, `#2` on the second of two identical ones. */
export const deviceLabel = d => `${d?.name || 'Unknown device'}${d?.index ? ` #${d.index + 1}` : ''}`
